///<amd-module name="Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/CopilotSidebar/CopilotSidebar" />
import * as React from "react";
import * as services from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/services/agentServices";
import { EmptyState, ImageMessagessConversation } from "@medius/ui-controls";
import { getLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { PredefinedQuestions } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/PredefinedQuestionButtons";
import { PromptInput } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/PromptInput";
import { MessageList } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/MessageList";
import { CopilotEventPayload, ILastChatRequestDetails, IProcessedMessage, Role, StreamCallScenario } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/agentInterfaces";
import { useCopilotContext } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/hooks/useCopilotContext";
import { whyHaveIReceivedThisInvoice, whyHaveIReceivedThisInvoiceQuestionId } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/AgentConsts";
import { TimeoutErrorMessage } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/TimeoutErrorMessage";
import { trackSentCopilotPromptEventInCandu } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/utils";

export interface ICopilotSidebarProps{
    getDelegationMessage: () => string;
    getEscalationMessage: () => string;
    taskId: number,
    copilotEventPayload: CopilotEventPayload | null;
    resetEventPayload: () => void;
}

export function CopilotSidebar({ getDelegationMessage, getEscalationMessage, taskId, copilotEventPayload, resetEventPayload }: ICopilotSidebarProps){
    const { 
        isOperational,
        predefinedQuestions, 
        setMessages,
        messages,
        documentId,
        workflowStepName,
        invoiceType,
        setProcessedMessage,
        isStreamActive,
        setIsStreamActive,
        setAbortController,
        setRequestTimeout,
        isAgentAvailable,
        isLoadingMessages,
        conversationId,
    } = useCopilotContext();
    const promptInputRef = React.useRef(null);
    const [latestChatCallDetails, setLatestChatCallDetails] = React.useState<ILastChatRequestDetails | null>(null); //retry mechanism details - originally nullified, when no previous operations were made
    const [isTimeoutError, setIsTimeoutError] = React.useState<boolean>(false);

    const handleStreamEnd = React.useCallback(() => {
        setIsStreamActive(false);
    }, [setIsStreamActive]);

    const addMessage = React.useCallback((content: string) => {
        setMessages(prev => [...prev, {
            isError: false,
            content: content,
            role: Role.user,
            conversationId: conversationId,
            documentId: documentId,
            workflowStepName: workflowStepName,
            invoiceType: invoiceType,
            date: new Date().toISOString(),
            sources: [],
            taskId: taskId
        }]);
    }, [conversationId, documentId, invoiceType, setMessages, taskId, workflowStepName]);

    const addErrorMessage = React.useCallback(() => {
        setMessages(prev => [...prev, {
            isError: true,
            content: "",
            role: Role.assistant,
            conversationId: conversationId,
            documentId: documentId,
            workflowStepName: workflowStepName,
            invoiceType: invoiceType,
            date: new Date().toISOString(),
            sources: [],
            taskId: taskId
        }]);
    }, [conversationId, documentId, invoiceType, setMessages, taskId, workflowStepName]);

    const handleMessageChunk = React.useCallback((message: IProcessedMessage) => {
        setProcessedMessage({
            ...message,
            content: getLabelTranslation(message.content),
            role: Role.assistant,
            conversationId: conversationId,
            documentId: documentId,
            taskId: taskId
        });
    }, [conversationId, documentId, setProcessedMessage, taskId]);

    const bypassBackendCallWithLocalMessage = React.useCallback((localMessage: string, respondingToPredefinedQuestionId?: number) => {
        setTimeout(() => {
            handleMessageChunk({
                content: localMessage,
                role: Role.user,
                conversationId: conversationId,
                documentId: documentId,
                date: new Date().toISOString(),
                sources: [],
                taskId: taskId,
                responseToPredefinedQuestionId: respondingToPredefinedQuestionId,
                analyzingImages: false
            });
            handleStreamEnd();
        }, 500);
    }, [conversationId, documentId, handleMessageChunk, handleStreamEnd, taskId]);

    const getPredefinedQuestionCategorizationId = React.useCallback((predefinedQuestion: string) => {
        return predefinedQuestions.find(question => question.value === predefinedQuestion).categorizationId;
    }, [predefinedQuestions]);

    
    const sendChatRequest = React.useCallback(async (prompt: string, conversationId: string, streamCallOrigin: StreamCallScenario, isFollowupQuestion: boolean) => {
        setLatestChatCallDetails({
            conversationId: conversationId,
            prompt: prompt,
            scenario: streamCallOrigin
        });
        setIsStreamActive(true);
        setIsTimeoutError(false);

        const abortControllerInstance = new AbortController();
        setAbortController(abortControllerInstance);
        const disablePredefinedQuestions =  predefinedQuestions == undefined || predefinedQuestions.length === 0;

        const timeout = setTimeout(() => {
            abortControllerInstance.abort();
            handleStreamEnd();
            setIsTimeoutError(true);
        }, 120_000);
        setRequestTimeout(timeout);

        try {
            await services.streamResponse(
                documentId,
                taskId,
                workflowStepName,
                invoiceType,
                getDelegationMessage(),
                getEscalationMessage(),
                isOperational,
                streamCallOrigin === "predefined" ? getLabelTranslation(prompt) : prompt,
                conversationId,
                messages,
                handleMessageChunk,
                handleStreamEnd,
                abortControllerInstance.signal,
                timeout,
                disablePredefinedQuestions,
                isFollowupQuestion,
                streamCallOrigin === "predefined" ? getPredefinedQuestionCategorizationId(prompt) : null

            );
        } catch (error) { addErrorMessage(); }

        trackSentCopilotPromptEventInCandu(streamCallOrigin);
    }, [addErrorMessage, documentId, getDelegationMessage, getEscalationMessage, getPredefinedQuestionCategorizationId, handleMessageChunk, handleStreamEnd, invoiceType, isOperational, messages, predefinedQuestions, setAbortController, setIsStreamActive, setRequestTimeout, taskId, workflowStepName]);

    const sendPredefinedQuestion = React.useCallback(async (predefinedQuestion: string, isRetry: boolean = false) => {
        if (isStreamActive)
            return;

        if(!isRetry)
            addMessage(getLabelTranslation(predefinedQuestion));

        if(predefinedQuestion === whyHaveIReceivedThisInvoice){
            const delegationMessage = getDelegationMessage();
            const escalationMessage = getEscalationMessage();
            const localMessage = delegationMessage || escalationMessage;
            if(localMessage){
                return bypassBackendCallWithLocalMessage(localMessage, whyHaveIReceivedThisInvoiceQuestionId);
            }
        }

        sendChatRequest(predefinedQuestion, conversationId, "predefined", false);
    }, [addMessage, bypassBackendCallWithLocalMessage, conversationId, getDelegationMessage, getEscalationMessage, isStreamActive, sendChatRequest]);

    const sendFreetextQuestion = React.useCallback(async (freetextQuestion: string, isFollowupQuestion: boolean) => {
        if (freetextQuestion.length === 0 || freetextQuestion.trim().length === 0 || isStreamActive)
            return;

        const truncatedPrompt = freetextQuestion.substring(0, 2000);
        addMessage(truncatedPrompt);
        sendChatRequest(truncatedPrompt, conversationId, "freetext", isFollowupQuestion);
    }, [conversationId, isStreamActive, addMessage, sendChatRequest]);

    const handleStreamRetry = () => {
        setMessages(prev => {
            const newMessages = [...prev];
            newMessages.pop();
            return newMessages;
        });

        if(!latestChatCallDetails)
            return;

        sendChatRequest(latestChatCallDetails.prompt, latestChatCallDetails.conversationId, latestChatCallDetails.scenario, false);
    };

    React.useEffect(() => {
        if(copilotEventPayload === null || messages.length === 0) {
            return;
        }

        if (copilotEventPayload.prompt) {
            sendFreetextQuestion(copilotEventPayload.prompt, false);
        } else if (copilotEventPayload.questionId) {
            const predefinedQuestionLabel = predefinedQuestions.filter(pq => pq.categorizationId === copilotEventPayload.questionId)[0];
            sendPredefinedQuestion(predefinedQuestionLabel.value, false);
        }
        resetEventPayload();
    }, [copilotEventPayload, messages, predefinedQuestions, resetEventPayload, sendFreetextQuestion, sendPredefinedQuestion]);

    if(!isAgentAvailable){
        return <EmptyState
            explanation={getLabelTranslation("#Core/agentUnavailableExplanation")}
            size="large"
            title={getLabelTranslation("#Core/agentUnavailableTitle")}
            image={<ImageMessagessConversation/>}
            centerInContainer={true}
            contentAlign="center"
        />;
    }

    return (
        <div className="mediusAgent__innerContainer" data-testid="mediusAgent__innerContainer">
            {isTimeoutError && <TimeoutErrorMessage/>}
            <MessageList promptInputRef={promptInputRef} retryCallback={handleStreamRetry} sendFreetextQuestion={sendFreetextQuestion} />
            { isLoadingMessages ? <></> : 
                <div className="mediusAgent__innerContainer__bottom" data-testid="mediusAgent__bottomContainer">
                    <PredefinedQuestions
                        predefinedQuestionsList={predefinedQuestions}
                        disabled={isStreamActive}
                        onPredefinedQuestionClick={sendPredefinedQuestion}
                    />
                    <PromptInput promptInputRef={promptInputRef} sendFreetextQuestion={sendFreetextQuestion} sendPredefinedQuestion={sendPredefinedQuestion} />
                </div>
            }
        </div>
    );
}